import { DonutChart } from '@carbon/charts-react';
import * as React from 'react';
import { Body, Heading } from 'vet-bones/design-system/components';

import {
  SduBreakdownItem,
  formatTotalUnits,
} from 'src/ui/components/sdu-dashboard/aggregations';
import {
  GROUP_KEYS,
  ModuleGroupKey,
  moduleGroupCssVar,
  readGroupColors,
} from 'src/ui/components/sdu-dashboard/modules';
import {
  SDU_DASHBOARD_BREAKDOWN,
  SDU_DASHBOARD_BREAKDOWN_LEGEND_LABEL,
  SDU_DASHBOARD_BREAKDOWN_LEGEND_VALUE,
  SDU_DASHBOARD_BREAKDOWN_PLACEHOLDER,
} from 'src/ui/constants/testIds';
import * as copy from 'src/ui/templates/copy';

const CHART_HEIGHT = '200px';
// Slices below this share are dropped from the legend and the donut so
// rounding can't produce a "0%" label.
const MIN_VISIBLE_PERCENT = 0.5;

const labelFor = (group: ModuleGroupKey): string =>
  copy.components.sduDashboard.groupLabels[group] ?? group;

interface SduConsumptionBreakdownProps {
  breakdown: SduBreakdownItem[];
  isLoading?: boolean;
}

export const SduConsumptionBreakdown: React.VFC<SduConsumptionBreakdownProps> = ({
  breakdown,
  isLoading,
}) => {
  const placeholderText = isLoading
    ? copy.components.sduDashboard.loading
    : copy.components.sduDashboard.empty;

  const groupColors = React.useMemo(() => readGroupColors(), []);

  const legendItems = React.useMemo(() => {
    const total = breakdown.reduce(
      (sum, item) => sum + item.totalUnitsConsumed,
      0
    );
    const orderIndex = (group: ModuleGroupKey): number => {
      const idx = GROUP_KEYS.indexOf(group);
      return idx === -1 ? GROUP_KEYS.length : idx;
    };
    const sorted = [...breakdown].sort(
      (a, b) => orderIndex(a.group) - orderIndex(b.group)
    );
    // Drop slices whose share is below the threshold so neither the legend
    // nor the donut renders a "0%" label after rounding.
    return sorted
      .map((item) => {
        const label = labelFor(item.group);
        const percent = total > 0 ? (item.totalUnitsConsumed / total) * 100 : 0;
        return {
          cssVar: moduleGroupCssVar(item.group),
          hex: groupColors[item.group] ?? '',
          label,
          percent,
          value: item.totalUnitsConsumed,
        };
      })
      .filter((item) => item.percent >= MIN_VISIBLE_PERCENT)
      .map((item) => ({ ...item, percent: Math.round(item.percent) }));
  }, [breakdown, groupColors]);

  const tabularData = React.useMemo(
    () =>
      legendItems.map((item) => ({
        group: item.label,
        value: item.value,
      })),
    [legendItems]
  );

  const colorScale = React.useMemo(() => {
    const scale: Record<string, string> = {};
    legendItems.forEach((item) => {
      if (item.hex) {
        scale[item.label] = item.hex;
      }
    });
    return scale;
  }, [legendItems]);

  const chartOptions = React.useMemo(() => {
    const orderedLabels = legendItems.map((item) => item.label);
    const labelIndex = (group: string): number => {
      const idx = orderedLabels.indexOf(group);
      return idx === -1 ? orderedLabels.length : idx;
    };
    return {
      color: { scale: colorScale },
      donut: {
        alignment: 'center',
        center: { label: '' },
      },
      height: CHART_HEIGHT,
      legend: { enabled: false },
      pie: {
        sortFunction: (
          a: { group: string; value: number },
          b: { group: string; value: number }
        ) => labelIndex(a.group) - labelIndex(b.group),
      },
      resizable: true,
      toolbar: { enabled: false },
    };
  }, [colorScale, legendItems]);

  return (
    <div className="organization-dashboard-card">
      <Heading size="md" testId={SDU_DASHBOARD_BREAKDOWN} weight="semibold">
        {copy.components.sduDashboard.breakdownTitle}
      </Heading>
      {!legendItems.length ? (
        <div className="sd-sdu-dashboard-breakdown-placeholder">
          <Body size="sm" testId={SDU_DASHBOARD_BREAKDOWN_PLACEHOLDER}>
            {placeholderText}
          </Body>
        </div>
      ) : (
        <div className="sd-sdu-dashboard-breakdown-content">
          <div className="sd-sdu-dashboard-breakdown-chart">
            <DonutChart data={tabularData} options={chartOptions} />
          </div>
          <ul className="sd-sdu-dashboard-breakdown-legend">
            {legendItems.map((item, idx) => (
              <li
                key={item.label}
                className="sd-sdu-dashboard-breakdown-legend-item"
              >
                <span
                  aria-hidden
                  className="sd-sdu-dashboard-breakdown-legend-swatch"
                  style={{ backgroundColor: `var(${item.cssVar})` }}
                />
                <Body
                  size="sm"
                  testId={SDU_DASHBOARD_BREAKDOWN_LEGEND_LABEL(item.label, idx)}
                >
                  {item.label}
                </Body>
                <Body
                  className="sd-sdu-dashboard-breakdown-legend-value"
                  size="sm"
                  testId={SDU_DASHBOARD_BREAKDOWN_LEGEND_VALUE(item.label, idx)}
                >
                  {`${formatTotalUnits(item.value)} (${item.percent}%)`}
                </Body>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
